"use client";

import type { SupStoredTransmute } from "@/store/types";
import React, { useCallback, useMemo, useState } from "react";

import Web3TransmuteCancel from "@/components/Web3/Transmute/Cancel";
import Web3TransmuteFinished from "@/components/Web3/Transmute/Finished";
import Web3TransmuteMain from "@/components/Web3/Transmute/Main";
import Web3TransmuteRegisterVault from "@/components/Web3/Transmute/RegisterVault";

import useMutateTokenSuperpositionBalances from "@/hooks/mutators/useMutateTokenSuperpositionBalances";
import { useStoredDataDispatch } from "@/store";
import { getVaultDecimals } from "@/utils";
import { BigNumber, FixedNumber } from "@ethersproject/bignumber";
import { SupText } from "@repo/core-ui";
import { twMerge } from "tailwind-merge";

interface Web3TransmuteFlowProps {
  className?: string;
  headerClassName?: string;
  transmuteTarget: SupStoredTransmute;
}

export default function Web3TransmuteFlow({ className, headerClassName, transmuteTarget }: Web3TransmuteFlowProps) {
  const storedDispatch = useStoredDataDispatch();

  const singleVault = transmuteTarget.superposition.vault;

  const decimals = getVaultDecimals(singleVault);

  const [totalAmount, updateTotalAmount] = useState<string>(
    transmuteTarget.amount ? transmuteTarget.amount : FixedNumber.fromValue(BigNumber.from(transmuteTarget.superposition.superposition_balance), decimals).toString()
  );
  const [isTransmuted, setIsTransmuted] = useState<boolean>(false);
  const [isCancelClicked, setIsCancelClicked] = useState<boolean>(false);

  const { mutate } = useMutateTokenSuperpositionBalances();

  const { price, balance } = useMemo(() => {
    const balance = FixedNumber.fromValue(BigNumber.from(`${transmuteTarget.superposition.superposition_balance ?? "1"}`), decimals);
    const balanceInFiat = FixedNumber.from(parseFloat(`${transmuteTarget.superposition.superposition_usd_value ?? "0"}`).toFixed(8));
    return {
      price: parseFloat(balanceInFiat.divUnsafe(balance).toString()),
      balance,
      balanceInFiat,
    };
  }, [decimals, transmuteTarget.superposition.superposition_balance, transmuteTarget.superposition.superposition_usd_value]);

  const balanceObject = useMemo(() => {
    if (!transmuteTarget) {
      return undefined;
    }

    return {
      symbol: transmuteTarget.superposition.vault.assets[0].symbol,
      decimals,
      amount: balance.toString(),
      price: parseFloat(price.toString()),
      icon: transmuteTarget.superposition.vault.assets[0].image,
    };
  }, [balance, decimals, price, transmuteTarget]);

  const sidebarTitle = useMemo(() => {
    if (isCancelClicked) {
      return "Cancel Rewards?";
    }
    return isTransmuted ? "Success" : "Transaction";
  }, [isCancelClicked, isTransmuted]);

  const handleClose = useCallback(() => {
    storedDispatch({ type: "clearTransmute" });
    storedDispatch({ type: "closeOverlay" });
  }, [storedDispatch]);

  const handleFinished = useCallback(() => {
    setTimeout(() => {
      mutate();
    }, 1000);
    setIsTransmuted(true);
  }, [mutate]);

  const handleWithdraw = useCallback(() => {
    storedDispatch({
      type: "setTxCartValue",
      txCartValue: {
        cart: "withdraw",
        totalAmount: totalAmount,
        selectedBalance: transmuteTarget.targetToken,
      },
    });
    storedDispatch({ type: "clearWithdrawCart" });
    storedDispatch({ type: "toggleWithdrawCart", balance: { ...transmuteTarget.superposition, is_erc20: true }, vault: transmuteTarget.superposition.vault });
    storedDispatch({ type: "setOverlay", overlay: "withdraw" });
    storedDispatch({ type: "clearTransmute" });
  }, [storedDispatch, totalAmount, transmuteTarget.superposition, transmuteTarget.targetToken]);

  const handleBaseCancel = useCallback(() => {
    if (transmuteTarget.source === "deposit") {
      setIsCancelClicked(true);
    } else {
      handleClose();
    }
  }, [handleClose, transmuteTarget.source]);

  return (
    <section className={twMerge("relative flex h-full max-h-screen flex-col overflow-y-scroll p-6 pt-[60px]", className)}>
      <header className={twMerge("fixed top-0 z-50 flex w-full items-center justify-start space-x-2 bg-neutral py-2.5", headerClassName)}>
        <SupText variant="subtitle1" weight="bold" className="leading-10">
          {sidebarTitle}
        </SupText>
      </header>
      {isTransmuted ? (
        <Web3TransmuteFinished
          type={transmuteTarget.direction}
          closeLabel={transmuteTarget.source === "withdraw" ? "cancel" : undefined}
          handleClose={handleClose}
          title={transmuteTarget.source === "deposit" ? "Rewards Active" : undefined}
          handleWithdraw={transmuteTarget.source === "withdraw" ? handleWithdraw : undefined}
        />
      ) : isCancelClicked ? (
        <Web3TransmuteCancel handleCancel={() => setIsCancelClicked(false)} handleOk={handleClose} />
      ) : (
        <Web3TransmuteRegisterVault handleCancel={handleClose} superPosition={transmuteTarget.superposition}>
          <Web3TransmuteMain
            balanceObject={balanceObject}
            transmuteTarget={transmuteTarget}
            value={totalAmount}
            onChange={value => updateTotalAmount(value || "0")}
            price={price}
            onCancel={handleBaseCancel}
            decimals={decimals}
            onFinished={handleFinished}
          />
        </Web3TransmuteRegisterVault>
      )}
    </section>
  );
}
